import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Crown, Lightbulb, Sparkles, Flag, CheckCircle2, Timer, ChevronLeft } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { CrosswordGrid } from "@/components/CrosswordGrid";
import { ReportModal } from "@/components/ReportModal";
import { AdBanner } from "@/components/AdBanner";
import { createWordStreak } from "@/lib/wordStreak";
import { createWordAudio } from "@/lib/wordStreakAudio";
import { createVictorySession } from "@/lib/victoryCelebration";
import { PuzzleResultsSharing } from "@/components/PuzzleResultsSharing";
import { VictoryCelebration } from "@/components/VictoryCelebration";

const emptyGrid = (size) => Array.from({ length: size }, () => Array.from({ length: size }, () => ""));

// Keep the mobile clue in the visible viewport without moving input focus.
function keepClueVisible(panel) {
  if (!panel) return;
  panel.scrollTop = 0;
  const mobile = typeof window.matchMedia === "function" ? window.matchMedia("(max-width: 1023px)") : null;
  const viewport = window.visualViewport;
  let frame = 0, releaseFrame = 0;
  let correcting = false, resizePending = false, lastAttempt = "";

  const check = () => {
    frame = 0;
    if (!mobile?.matches) {
      panel.style.removeProperty("--clue-top");
      panel.style.removeProperty("--clue-max-height");
      return;
    }
    const top = (viewport?.offsetTop || 0) + 64;
    const bottom = (viewport?.offsetTop || 0) + (viewport?.height || window.innerHeight) - 8;
    panel.style.setProperty("--clue-top", `${top}px`);
    panel.style.setProperty("--clue-max-height", `${Math.max(48, Math.min(160, bottom - top - 8))}px`);
    const rect = panel.getBoundingClientRect();
    const delta = rect.top < top - 1 ? rect.top - top : rect.bottom > bottom + 1 ? rect.bottom - bottom : 0;
    if (!delta) { lastAttempt = ""; return; }
    const target = Math.max(0, window.scrollY + delta);
    const attempt = [rect.top, rect.bottom, top, bottom].map(Math.round).join(":");
    if (correcting || attempt === lastAttempt || Math.abs(target - window.scrollY) <= 1) return;
    lastAttempt = attempt;
    correcting = true;
    window.scrollBy({ top: target - window.scrollY, behavior: "instant" });
    // Ignore scroll events caused by our correction, including the following paint.
    releaseFrame = requestAnimationFrame(() => {
      releaseFrame = requestAnimationFrame(() => {
        correcting = false;
        if (resizePending) { resizePending = false; schedule(); }
      });
    });
  };
  const schedule = (event) => {
    if (correcting) { if (event?.type === "resize") resizePending = true; return; }
    if (!frame) frame = requestAnimationFrame(check);
  };
  viewport?.addEventListener("resize", schedule);
  viewport?.addEventListener("scroll", schedule);
  window.addEventListener("resize", schedule);
  schedule();
  return () => {
    viewport?.removeEventListener("resize", schedule);
    viewport?.removeEventListener("scroll", schedule);
    window.removeEventListener("resize", schedule);
    cancelAnimationFrame(frame);
    cancelAnimationFrame(releaseFrame);
    panel.style.removeProperty("--clue-top");
    panel.style.removeProperty("--clue-max-height");
  };
}

export default function Play() {
  const { zone } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const puzzleId = searchParams.get("puzzle_id");
  const squabId = searchParams.get("squab");

  const [state, setState] = useState({ loading: true, locked: false, error: null });
  const [puzzle, setPuzzle] = useState(null);
  const [playId, setPlayId] = useState(null);
  const wordStreak = useMemo(() => createWordStreak(), [playId]);
  const wordAudio = useMemo(() => createWordAudio(), [playId, zone, puzzleId, squabId, user?.user_id]);
  const victorySession = useMemo(() => createVictorySession(), [zone, puzzleId, squabId, user?.user_id]);
  const [celebration, setCelebration] = useState(null);
  const dismissVictory = useCallback(() => {
    wordAudio.stop();
    setCelebration(null);
  }, [wordAudio]);
  useEffect(() => {
    victorySession.activate(() => wordAudio.stop());
    return () => { victorySession.dispose(); wordAudio.stop(); };
  }, [victorySession, wordAudio]);
  const [values, setValues] = useState([]);
  const [revealed, setRevealed] = useState(new Set());
  const [wrong, setWrong] = useState(new Set());
  const [active, setActive] = useState(null);
  const [direction, setDirection] = useState("across");
  const [seconds, setSeconds] = useState(0);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [showAds, setShowAds] = useState(false);
  const [lockedCells, setLockedCells] = useState(new Set());
  const [confirmedWords, setConfirmedWords] = useState(new Set());
  const [flashCells, setFlashCells] = useState(new Set());
  const [progressKey, setProgressKey] = useState(null);
  const [streakTick, setStreakTick] = useState(0);
  const savedStreak = useRef(null);
  const currentClueRef = useRef(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(squabId ? `/squab/${encodeURIComponent(squabId)}` : "/login"); return; }
    let cancel = false;
    victorySession.begin();
    setProgressKey(null);
    setCelebration(null);
    setResult(null);
    (async () => {
      setState({ loading: true, locked: false, error: null });
      try {
        const [{ data }, zres] = await Promise.all([
          squabId ? api.post(`/squab/${encodeURIComponent(squabId)}/start`) : api.post("/game/start", puzzleId ? { zone, puzzle_id: puzzleId } : { zone }),
          api.get("/game/zones"),
        ]);
        if (cancel) return;
        victorySession.arm([user.user_id, zone, data.puzzle.puzzle_id, data.date, data.play_id], data.already_solved);
        setPuzzle(data.puzzle);
        setPlayId(data.play_id);
        const g = emptyGrid(data.puzzle.size);
        const rev = new Set();
        (data.revealed || []).forEach((k) => rev.add(k));
        const pKey = `hhkd_progress:${user.user_id}:${zone}:${data.puzzle.puzzle_id}:${data.play_id}`;
        let saved = null;
        try { saved = JSON.parse(localStorage.getItem(pKey)); } catch { saved = null; }
        if (data.already_solved || saved?.values?.length !== data.puzzle.size) { try { localStorage.removeItem(pKey); } catch {} saved = null; }
        setValues(saved ? saved.values : g);
        setRevealed(rev);
        setLockedCells(new Set(saved?.locked || []));
        setConfirmedWords(new Set(saved?.confirmed || []));
        setSeconds(saved?.seconds || 0);
        savedStreak.current = saved?.streak ? { playId: data.play_id, ...saved.streak } : null;
        setFlashCells(new Set());
        if (!data.already_solved) setProgressKey(pKey);
        setShowAds(zres.data.ads_enabled_for_free && !zres.data.is_premium);
        if (data.already_solved) setResult({ perfect: true, score: data.best_score, correct: 0, total: 0, solvedBefore: true });
        setRunning(!data.already_solved);
        setState({ loading: false, locked: false, error: null });
        const first = data.puzzle.across[0] || data.puzzle.down[0];
        if (first) { setActive({ r: first.row, c: first.col }); setDirection(first.direction); }
      } catch (e) {
        if (cancel) return;
        if (e?.response?.status === 403) setState({ loading: false, locked: true, error: apiError(e) });
        else setState({ loading: false, locked: false, error: apiError(e) });
      }
    })();
    return () => { cancel = true; };
  }, [zone, puzzleId, squabId, user, authLoading, navigate, victorySession]);

  useEffect(() => {
    const s = savedStreak.current;
    if (s && s.playId === playId) { wordStreak.restore(s.count, s.confirmed); savedStreak.current = null; }
  }, [wordStreak, playId]);

  useEffect(() => {
    if (!progressKey) return;
    try { localStorage.setItem(progressKey, JSON.stringify({
      values, locked: [...lockedCells], confirmed: [...confirmedWords], seconds,
      streak: { count: wordStreak.count, confirmed: [...wordStreak.confirmed] },
    })); } catch {}
  }, [progressKey, values, lockedCells, confirmedWords, seconds, wordStreak, streakTick]);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  const size = puzzle?.size || 18;

  const white = (r, c) => r >= 0 && r < size && c >= 0 && c < size && puzzle.layout[r] && puzzle.layout[r][c];
  const cellHasDir = (cell, dir) => {
    const { r, c } = cell;
    if (dir === "across") return white(r, c - 1) || white(r, c + 1);
    return white(r - 1, c) || white(r + 1, c);
  };

  // --- Locking / progression helpers (frontend-only; scoring/timer untouched) ---
  const isLocked = (r, c) => lockedCells.has(`${r}-${c}`);
  const stepEditable = (r, c, dr, dc) => {
    let nr = r + dr, nc = c + dc;
    while (white(nr, nc) && isLocked(nr, nc)) { nr += dr; nc += dc; }
    return white(nr, nc) ? { r: nr, c: nc } : null;
  };
  const firstEditableCell = (cl, lset) => {
    const L = lset || lockedCells;
    const dr = cl.direction === "across" ? 0 : 1, dc = cl.direction === "across" ? 1 : 0;
    for (let i = 0; i < cl.length; i++) {
      const r = cl.row + dr * i, c = cl.col + dc * i;
      if (!L.has(`${r}-${c}`)) return { r, c };
    }
    return { r: cl.row, c: cl.col };
  };
  
const playLetterClick = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = window.__hhkdClickCtx || (window.__hhkdClickCtx = new AC());
    if (ctx.state === "suspended") ctx.resume();
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(650, now);
    osc.frequency.exponentialRampToValueAtTime(400, now + 0.025);
    gain.gain.setValueAtTime(0.025, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.035);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.04);
  } catch (e) {}
};

  const playChime = async (register = () => {}) => {
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = window.__hhkdClickCtx || (window.__hhkdClickCtx = new AC());
      if (ctx.state === "suspended") await ctx.resume();
      return new Promise(resolve => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = "sine";
        o.frequency.setValueAtTime(880, ctx.currentTime);
        o.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.22, ctx.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.35);
        o.onended = () => { o.disconnect(); g.disconnect(); resolve(); };
        if (register(o) === false) { o.disconnect(); g.disconnect(); resolve(); return; }
        o.start(); o.stop(ctx.currentTime + 0.38);
      });
    } catch {}
  };

  // opts: { dir } explicit direction; { focus } just set active cell (keep direction);
  //       { tap } pointer/space — toggle when re-tapping active cell, else pick a valid direction.
  const onSelect = (cell, opts = {}) => {
    const { dir, tap, focus } = opts;
    if (dir) { setDirection(dir); setActive(cell); return; }
    if (focus) {
      setActive(cell);
      // Keep the displayed clue/highlight consistent: if the focused cell isn't
      // part of a word in the current direction, switch to the direction it belongs to.
      if (!cellHasDir(cell, direction)) {
        const other = direction === "across" ? "down" : "across";
        if (cellHasDir(cell, other)) setDirection(other);
      }
      return;
    }
    if (tap) {
      const same = active && active.r === cell.r && active.c === cell.c;
      const other = direction === "across" ? "down" : "across";
      if (same) {
        if (cellHasDir(cell, other)) setDirection(other);
      } else {
        setDirection(cellHasDir(cell, direction) ? direction : (cellHasDir(cell, other) ? other : direction));
      }
      setActive(cell);
      return;
    }
    setActive(cell);
  };

  const onLetter = (r, c, letter) => {
    const dr = direction === "across" ? 0 : 1;
    const dc = direction === "across" ? 1 : 0;
    // Locked (confirmed) letters cannot be overwritten — skip past them.
    if (isLocked(r, c)) { const nxt = stepEditable(r, c, dr, dc); if (nxt) setActive(nxt); return; }
    setWrong(new Set());
    if (letter) playLetterClick();
const nextValues = values.map(row => row.slice());
nextValues[r][c] = letter;
setValues(nextValues);
if (letter && currentClue) checkCompletedWord(currentClue, nextValues);

    if (letter) { const nxt = stepEditable(r, c, dr, dc); if (nxt) setActive(nxt); }
  };

  const onBackspace = (r, c) => {
    setWrong(new Set());
    const dr = direction === "across" ? 0 : 1;
    const dc = direction === "across" ? 1 : 0;
    // Locked letters cannot be erased — move to the previous editable cell.
    if (isLocked(r, c)) { const p = stepEditable(r, c, -dr, -dc); if (p) setActive(p); return; }
    const hasVal = !!values[r][c];
    const prev = stepEditable(r, c, -dr, -dc);
    setValues((v) => {
      const nv = v.map((row) => row.slice());
      if (nv[r][c]) nv[r][c] = "";
      else if (prev) nv[prev.r][prev.c] = "";
      return nv;
    });
    if (!hasVal && prev) setActive(prev);
  };

  const currentClue = useMemo(() => {
    if (!puzzle || !active) return null;
    const list = direction === "across" ? puzzle.across : puzzle.down;
    return list.find((cl) => {
      if (direction === "across") return cl.row === active.r && active.c >= cl.col && active.c < cl.col + cl.length;
      return cl.col === active.c && active.r >= cl.row && active.r < cl.row + cl.length;
    });
  }, [puzzle, active, direction]);

  // Clue identity changes on manual selection and both automatic advance paths.
  // Cell-by-cell typing and timer/streak updates do not rerun this effect.
  useEffect(() => keepClueVisible(currentClueRef.current),
    [puzzle?.puzzle_id, currentClue?.number, currentClue?.direction, currentClue?.clue]);

  const selectClue = (cl) => { onSelect(firstEditableCell(cl), { dir: cl.direction }); };

const checkCompletedWord = async (clue, board) => {
  if (!clue) return;
  const key = `${clue.direction}-${clue.number}`;
  if (confirmedWords.has(key)) return;

  const dr = clue.direction === "across" ? 0 : 1;
  const dc = clue.direction === "across" ? 1 : 0;
  const cells = Array.from({ length: clue.length }, (_, i) => [
    clue.row + dr * i, clue.col + dc * i
  ]);
  if (!cells.every(([r, c]) => board[r]?.[c])) return;

  try {
    const victoryToken = victorySession.token;
    const { data } = await api.post("/game/check-word", {
      play_id: playId,
      number: clue.number,
      direction: clue.direction,
      answers: board
    });
    if (!victorySession.isCurrent(victoryToken)) return;
    const wasConfirmed = wordStreak.confirmed.has(key);
    const milestone = wordStreak.accept(key, data.correct);
    setStreakTick((t) => t + 1);
    if (data.correct && !wasConfirmed) wordAudio.play(playChime, milestone);
    if (!data.correct) return;

    const nextLocked = new Set(lockedCells);
    const flashed = new Set();
    cells.forEach(([r, c]) => {
      const id = `${r}-${c}`;
      if (!nextLocked.has(id)) flashed.add(id);
      nextLocked.add(id);
    });

    setLockedCells(nextLocked);
    setConfirmedWords(prev => new Set([...prev, key]));
    setFlashCells(flashed);
    setTimeout(() => setFlashCells(new Set()), 850);

    const nextClue = [...puzzle.across, ...puzzle.down].find(cl =>
      `${cl.direction}-${cl.number}` !== key &&
      !confirmedWords.has(`${cl.direction}-${cl.number}`) &&
      firstEditableCell(cl, nextLocked)
    );
    if (nextClue) {
      onSelect(firstEditableCell(nextClue, nextLocked),
        { dir: nextClue.direction });
    }
if (!nextClue) await submit(board);
    } catch (err) {
  console.error("Word check failed", err);
}
};

const onReveal = (cells) => {setValues((v) => { const nv = v.map((row) => row.slice()); cells.forEach((c) => (nv[c.row][c.col] = c.letter)); return nv; });
    setRevealed((s) => { const ns = new Set(s); cells.forEach((c) => ns.add(`${c.row}-${c.col}`)); return ns; });
  };

  const hint = async (kind) => {
    if (!currentClue) { toast.error("Select a clue first"); return; }
    try {
      const { data } = await api.post("/game/hint", {
        play_id: playId, number: currentClue.number, direction: currentClue.direction, kind,
        cell_index: direction === "across" ? active.c - currentClue.col : active.r - currentClue.row,
      });
      onReveal(data.cells);
      toast(`Hint used · -${data.penalty} pts`, { icon: "💡" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async (board = values) => {
    try {
      const victoryToken = victorySession.token;
      const { data } = await api.post("/game/submit", { play_id: playId, answers: board, time_seconds: seconds });
      if (!victorySession.isCurrent(victoryToken) || (data.perfect && victorySession.completed)) return;
      setResult(data);
      const wrongSet = new Set((data.wrong_cells || []).map((c) => `${c.row}-${c.col}`));

      // A word is confirmed when every cell is filled and none is wrong.
      const allClues = [...puzzle.across, ...puzzle.down];
      const nextLocked = new Set(lockedCells);
      const confirmedNowKeys = [];
      const newlyCells = [];
      allClues.forEach((cl) => {
        const dr = cl.direction === "across" ? 0 : 1, dc = cl.direction === "across" ? 1 : 0;
        let ok = true;
        const cells = [];
        for (let i = 0; i < cl.length; i++) {
          const r = cl.row + dr * i, c = cl.col + dc * i;
          cells.push([r, c]);
          if (!board[r][c] || wrongSet.has(`${r}-${c}`)) { ok = false; break; }
        }
        if (!ok) return;
        const key = `${cl.direction}-${cl.number}`;
        confirmedNowKeys.push(key);
        cells.forEach(([r, c]) => nextLocked.add(`${r}-${c}`));
        // Flash only cells that are newly confirmed this check (skip letters already locked
        // by a previously-confirmed crossing word) so celebration never replays.
        if (!confirmedWords.has(key)) cells.forEach(([r, c]) => { if (!lockedCells.has(`${r}-${c}`)) newlyCells.push(`${r}-${c}`); });
      });

      const newlyConfirmed = confirmedNowKeys.filter((k) => !confirmedWords.has(k) && !wordStreak.confirmed.has(k));
      wordStreak.batch(confirmedNowKeys, wrongSet.size > 0);
      setStreakTick((t) => t + 1);
      setLockedCells(nextLocked);
      setConfirmedWords((prev) => { const ns = new Set(prev); confirmedNowKeys.forEach((k) => ns.add(k)); return ns; });
      // Red highlight only for wrong cells — never on confirmed/locked cells.
      setWrong(new Set([...wrongSet].filter((k) => !nextLocked.has(k))));

      // Celebrate only NEWLY confirmed words, once per check (single sound + flash).
      if (newlyConfirmed.length > 0) {
        wordAudio.play(playChime);
        const fset = new Set(newlyCells);
        setFlashCells(fset);
        setTimeout(() => setFlashCells(new Set()), 850);
      }

      if (data.perfect) {
        setRunning(false);
        if (progressKey) { try { localStorage.removeItem(progressKey); } catch {} }
        setProgressKey(null);
        const voice = victorySession.claim(data);
        if (voice) {
          setCelebration({ ...voice, score: data.score, session: victorySession });
          wordAudio.playRecording(voice.path);
        }
        toast.success(`Solved! +${data.score} pts`);
        (data.unlocked_achievements || []).forEach((a) => toast.success(`🏆 ${a.name} unlocked!`));
      } else {
        toast.error(`${data.correct}/${data.total} correct — keep going!`);
        // Progress to the next unsolved Across clue (then Down), focusing its first editable cell.
        const confirmedKeys = new Set([...confirmedWords, ...confirmedNowKeys]);
        const pick = (list) => list.find((cl) => !confirmedKeys.has(`${cl.direction}-${cl.number}`));
        const target = pick(puzzle.across) || pick(puzzle.down);
        if (target) onSelect(firstEditableCell(target, nextLocked), { dir: target.direction });
      }
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const mmss = `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

  if (state.loading) return <Layout><div className="py-32 text-center font-mono-caps text-xs text-primary animate-pulse">Loading the grid…</div></Layout>;

  if (state.locked) return (
    <Layout>
      <div className="max-w-lg mx-auto px-5 py-24 text-center" data-testid="zone-locked">
        <Lock className="mx-auto text-primary mb-6" size={40} />
        <h1 className="font-display text-4xl mb-3">TRIPLE OG</h1>
        <p className="text-muted-foreground mb-8">{state.error || "This zone is part of Premium."} Unlock Triple OG for ${"2.99"}/mo.</p>
        <button onClick={() => navigate("/premium")} data-testid="locked-upgrade" className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-3.5 border border-primary hover-lift">
          <Crown size={15} /> Go Premium
        </button>
      </div>
    </Layout>
  );

  if (state.error) return <Layout><div className="py-32 text-center text-muted-foreground">{state.error}</div></Layout>;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8">
        <button onClick={() => navigate(puzzleId ? "/archive" : "/")} className="font-mono-caps text-[10px] text-muted-foreground hover:text-primary flex items-center gap-1 mb-4"><ChevronLeft size={13} /> {puzzleId ? "Archive" : "All Zones"}</button>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="font-mono-caps text-[10px] text-primary mb-1">{puzzle.title}</p>
            <h1 className="font-display text-3xl sm:text-4xl">{squabId ? "SQUAB WIT DA HOMIE" : "DAILY CROSSWORD"}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 border border-white/20 px-4 py-2 font-mono-caps text-sm" data-testid="timer"><Timer size={15} className="text-primary" /> {mmss}</div>
            <div className="flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-1 border border-primary/40 bg-primary/10 px-3 py-2 font-mono-caps text-[10px] sm:text-xs" data-testid="word-streak" aria-live="polite" aria-atomic="true">
              <span className="whitespace-nowrap">🔥 {wordStreak.count} WORD STREAK</span>
              {({ 3: "BOO-YAH", 5: "BOO-YAKA", 10: "TAA-DOW" })[wordStreak.count] && (
                <span>{({ 3: "BOO-YAH", 5: "BOO-YAKA", 10: "TAA-DOW" })[wordStreak.count]}</span>
              )}
            </div>
            <button onClick={() => setShowReport(true)} data-testid="report-btn" className="flex items-center gap-1.5 border border-white/20 px-3 py-2 font-mono-caps text-[10px] hover:border-destructive hover:text-destructive transition-colors"><Flag size={13} /> Report</button>
          </div>
        </div>

        {showAds && <div className="mb-6"><AdBanner testId="play-ad" /></div>}

        {result && result.perfect && (
          <div className="mb-6 border-2 border-secondary bg-secondary/10 p-5 flex items-center gap-4 fade-up" data-testid="solved-banner">
            <CheckCircle2 className="text-secondary" size={28} />
            <div><div className="font-display text-2xl">{result.solvedBefore ? "ALREADY SOLVED" : "PUZZLE SOLVED!"}</div>
              <div className="font-mono-caps text-[10px] text-muted-foreground">Score {result.score} pts</div></div>
          </div>
        )}

        {result?.perfect && (squabId ? (
          <button onClick={() => navigate(`/squab/${encodeURIComponent(squabId)}`)} className="mb-6 border border-primary px-5 py-3 text-primary font-mono-caps text-xs">View Battle & Share Results</button>
        ) : zone === "casual" && !celebration ? <div className="mb-6 max-w-2xl"><PuzzleResultsSharing playId={playId} result={{ player: user?.name, title: puzzle.title, puzzleId: puzzle.puzzle_id, score: result.score, seconds: result.solvedBefore ? null : seconds }} /></div> : null)}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 flex min-w-0 flex-col">
            <div className="order-2 lg:order-1">
              <CrosswordGrid layout={puzzle.layout} size={size} values={values} revealed={revealed} wrong={wrong}
                active={active} direction={direction} locked={lockedCells} flash={flashCells} onSelect={onSelect} onLetter={onLetter} onBackspace={onBackspace} />
            </div>

            {currentClue && (
              <div ref={currentClueRef} className="sticky top-[var(--clue-top,64px)] z-40 order-1 mb-4 max-h-[var(--clue-max-height,160px)] overflow-y-auto overscroll-contain break-words border border-primary/40 bg-card p-4 lg:static lg:order-2 lg:mb-0 lg:mt-4 lg:max-h-none lg:overflow-visible" data-testid="current-clue">
                <span className="font-mono-caps text-[9px] text-primary">{currentClue.number} {currentClue.direction}</span>
                <p className="text-base mt-1">{currentClue.clue}</p>
              </div>
            )}

            <div className="order-3 mt-4 flex flex-wrap gap-3">
              <button onClick={() => hint("letter")} data-testid="hint-letter-btn" className="flex items-center gap-1.5 border border-white/20 px-4 py-2.5 font-mono-caps text-[10px] hover:border-primary hover:text-primary transition-colors">
                <Lightbulb size={14} /> Reveal Letter
              </button>
              <button onClick={() => hint("word")} data-testid="hint-word-btn" className="flex items-center gap-1.5 border border-white/20 px-4 py-2.5 font-mono-caps text-[10px] hover:border-primary hover:text-primary transition-colors">
                <Sparkles size={14} /> Reveal Word {!user?.is_premium && <Lock size={11} className="text-primary" />}
              </button>
              <button onClick={() => submit()}  data-testid="submit-btn" className="flex-1 min-w-[160px] bg-primary text-primary-foreground font-mono-caps text-xs px-6 py-2.5 border border-primary hover-lift">
                Check Answers
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-1 gap-4">
            <ClueColumn title="Across" clues={puzzle.across} active={active} direction={direction} onPick={selectClue} dir="across" />
            <ClueColumn title="Down" clues={puzzle.down} active={active} direction={direction} onPick={selectClue} dir="down" />
          </div>
        </div>
      </div>
      {celebration?.session === victorySession && <VictoryCelebration celebration={celebration} onDismiss={dismissVictory} playId={playId}
        shareResult={zone === "casual" && !squabId ? { player: user?.name, title: puzzle.title, puzzleId: puzzle.puzzle_id, score: celebration.score, seconds } : null} />}
      {showReport && <ReportModal puzzleId={puzzle.puzzle_id} clueRef={currentClue ? `${currentClue.number}-${currentClue.direction}` : null} onClose={() => setShowReport(false)} />}
    </Layout>
  );
}

function ClueColumn({ title, clues, active, direction, onPick, dir }) {
  const isActive = (cl) => {
    if (!active || direction !== dir) return false;
    if (dir === "across") return cl.row === active.r && active.c >= cl.col && active.c < cl.col + cl.length;
    return cl.col === active.c && active.r >= cl.row && active.r < cl.row + cl.length;
  };
  return (
    <div className="border border-white/15 bg-card">
      <div className="font-mono-caps text-[10px] text-primary px-4 py-3 border-b border-white/10">{title}</div>
      <div className="max-h-[320px] overflow-auto no-scrollbar">
        {clues.map((cl) => (
          <button key={`${cl.number}-${dir}`} onClick={() => onPick(cl)} data-testid={`clue-${dir}-${cl.number}`}
            className={`w-full text-left px-4 py-2 text-sm border-b border-white/5 flex gap-2 transition-colors ${isActive(cl) ? "bg-primary/15 text-foreground" : "hover:bg-white/5 text-muted-foreground"}`}>
            <span className="font-bold text-foreground w-6 shrink-0">{cl.number}</span> {cl.clue}
          </button>
        ))}
      </div>
    </div>
  );
}
